/**
 * Socket handler registry for Aegix One.
 *
 * Combines all domain-specific handler maps and exposes a message parser
 * that normalizes the server's envelope shape.
 */

import type { HandlerDeps, SocketHandlerMap } from './types';
import { createReunificationHandlers } from './reunificationHandlers';

export type { HandlerDeps, SocketHandler, SocketHandlerMap } from './types';

export function createSocketHandlers(deps: HandlerDeps): SocketHandlerMap {
  return {
    ...createReunificationHandlers(deps),
  };
}

export interface ParsedSocketMessage {
  type: string;
  data: unknown;
  raw: unknown;
}

/**
 * Parse a raw WebSocket text frame into `{ type, data, raw }`.
 *
 * The backend envelope varies:
 *  - Legacy: `{ "type": "...", "data": "<json-string>" }`
 *  - Newer:  `{ "type": "...", "data": { ... } }` or the inner payload
 *    inlined at the top level.
 *
 * We accept both `data` shapes and fall through to `null` on failure.
 */
export function parseSocketMessage(raw: string): ParsedSocketMessage | null {
  if (!raw || raw.length < 2) return null;

  let envelope: { type?: string; data?: unknown };
  try {
    envelope = JSON.parse(raw);
  } catch (error) {
    console.error('[SocketHandlers] Failed to parse message:', error);
    return null;
  }

  if (!envelope || typeof envelope.type !== 'string') return null;

  let data: unknown = envelope.data ?? null;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      // Leave as string if it's not JSON-encoded.
    }
  }

  return { type: envelope.type, data, raw: envelope };
}
